import { Component, ElementRef, EventEmitter, inject, Input, NgZone, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { RoomScanService } from 'src/providers/room-scan/room-scan';

type THREE = typeof import('three');
type OrbitControlsType = import('three/examples/jsm/controls/OrbitControls.js').OrbitControls;

@Component({
  selector: 'tidy-floorplan-viewer',
  templateUrl: './floorplan-viewer.component.html',
  styleUrls: ['./floorplan-viewer.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true
})
export class FloorplanViewerComponent implements OnInit, OnDestroy {
  @ViewChild('canvasContainer', { static: true }) canvasContainer: ElementRef<HTMLDivElement>;
  @Input() usdzUrl: string | null = null;
  @Input() isDemo = false;
  @Output() createFloorplan = new EventEmitter<void>();
  @Output() uploadFloorplan = new EventEmitter<void>();
  @Output() zoomOutToMap = new EventEmitter<void>();

  private THREE: THREE;
  private scene: any;
  private camera: any;
  private renderer: any;
  private controls: OrbitControlsType;
  private animationFrameId: number;
  private hasZoomedIn = false;
  private hasEmittedZoomOut = false;
  private model: any;
  private boundOnWindowResize: () => void;
  private boundAnimate: () => void;
  private needsRender = true;
  private dampingTimeout: ReturnType<typeof setTimeout>;

  isLoading = true;
  loadError: string | null = null;
  isIOS = false;
  supportsAR = false;

  private roomScanService = inject(RoomScanService);

  constructor(private ngZone: NgZone) {
    this.isIOS = Capacitor.getPlatform() === 'ios' || /iPad|iPhone|iPod/.test(navigator.userAgent);
    this.supportsAR = this.checkARSupport();
  }

  private checkARSupport(): boolean {
    if (this.isIOS) {
      const anchor = document.createElement('a');
      return anchor.relList?.supports?.('ar') ?? true;
    }
    return false;
  }

  async ngOnInit(): Promise<void> {
    await this.loadThreeJS();
    await this.initThreeJS();
    this.loadModel();
  }

  private async loadThreeJS(): Promise<void> {
    this.THREE = await import('three');
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  private async initThreeJS(): Promise<void> {
    const container = this.canvasContainer.nativeElement;
    const width = container.clientWidth || window.innerWidth;
    const height = container.clientHeight || window.innerHeight;
    const { OrbitControls } = await import('three/examples/jsm/controls/OrbitControls.js');

    this.scene = new this.THREE.Scene();
    this.scene.background = new this.THREE.Color(0x1a1a1f);

    this.camera = new this.THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    this.camera.position.set(5, 5, 5);

    this.renderer = new this.THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'low-power' });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    this.renderer.toneMapping = this.THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;
    container.appendChild(this.renderer.domElement);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.enableZoom = true;
    this.controls.enablePan = false;
    this.controls.minDistance = 2;
    this.controls.maxDistance = 50;
    this.controls.maxPolarAngle = Math.PI / 2;
    this.controls.addEventListener('change', () => this.requestRender());
    this.controls.addEventListener('start', () => this.startContinuousRender());
    this.controls.addEventListener('end', () => this.stopContinuousRender());

    this.addLights();
    this.addGrid();

    this.boundOnWindowResize = this.onWindowResize.bind(this);
    this.boundAnimate = this.animate.bind(this);
    window.addEventListener('resize', this.boundOnWindowResize);
    this.requestRender();
  }

  private requestRender(): void {
    this.needsRender = true;
    if (!this.animationFrameId) {
      this.animationFrameId = requestAnimationFrame(this.boundAnimate);
    }
  }

  private startContinuousRender(): void {
    clearTimeout(this.dampingTimeout);
    this.needsRender = true;
    if (!this.animationFrameId) {
      this.animationFrameId = requestAnimationFrame(this.boundAnimate);
    }
  }

  private stopContinuousRender(): void {
    this.dampingTimeout = setTimeout(() => {
      this.needsRender = false;
    }, 500);
  }

  private addLights(): void {
    const ambientLight = new this.THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(ambientLight);

    const directionalLight = new this.THREE.DirectionalLight(0xffffff, 1.2);
    directionalLight.position.set(10, 20, 10);
    this.scene.add(directionalLight);

    const fillLight = new this.THREE.DirectionalLight(0x00aaba, 0.3);
    fillLight.position.set(-5, 5, -5);
    this.scene.add(fillLight);

    const rimLight = new this.THREE.DirectionalLight(0xffffff, 0.4);
    rimLight.position.set(0, 10, -10);
    this.scene.add(rimLight);
  }

  private addGrid(): void {
    const gridHelper = new this.THREE.GridHelper(20, 20, 0x00aaba, 0x333340);
    gridHelper.position.y = -0.01;
    this.scene.add(gridHelper);
  }

  private async loadModel(): Promise<void> {
    this.isLoading = true;
    this.loadError = null;

    try {
      if (this.usdzUrl) {
        await this.loadUSDZModel(this.usdzUrl);
      } else if (this.isDemo) {
        await this.loadDemoModel();
      } else {
        this.isLoading = false;
      }
    } catch (error) {
      console.error('Error loading 3D model:', error);
      this.loadError = 'Unable to load 3D floorplan. This feature requires a modern browser with WebAssembly support.';
      this.isLoading = false;
    }
  }

  private async loadUSDZModel(url: string): Promise<void> {
    const usdzModule = await import('three-usdz-loader');
    // Handle CommonJS/ESM interop - the export could be at different paths
    const USDZLoader = (usdzModule as any).USDZLoader ||
                       (usdzModule as any).default?.USDZLoader ||
                       (usdzModule as any).default;

    if (!USDZLoader || typeof USDZLoader !== 'function') {
      console.error('Failed to load USDZLoader. Module structure:', Object.keys(usdzModule));
      throw new Error('USDZLoader not available');
    }

    const loader = new USDZLoader('assets/usdz-wasm');
    const group = new this.THREE.Group();
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const file = new File([blob], 'model.usdz', { type: 'model/vnd.usdz+zip' });
      await loader.loadFile(file, group);
      this.addModelToScene(group);
      this.isLoading = false;
    } catch (error) {
      console.error('USDZ load error:', error);
      throw error;
    }
  }

  private async loadDemoModel(): Promise<void> {
    await this.loadUSDZModel('assets/sample-floorplan.usdz');
  }

  private addModelToScene(model: any): void {
    if (this.model) {
      this.scene.remove(this.model);
    }

    this.model = model;
    this.scene.add(model);

    const box = new this.THREE.Box3().setFromObject(model);
    const center = box.getCenter(new this.THREE.Vector3());
    const size = box.getSize(new this.THREE.Vector3());

    model.position.sub(center);
    model.position.y += size.y / 2;

    const maxDim = Math.max(size.x, size.y, size.z);
    const cameraDistance = maxDim * 2;
    this.camera.position.set(cameraDistance, cameraDistance, cameraDistance);
    this.controls.target.set(0, size.y / 4, 0);
    this.controls.update();
    this.requestRender();
  }

  private animate(): void {
    if (!this.needsRender) {
      this.animationFrameId = null;
      return;
    }
    this.animationFrameId = requestAnimationFrame(this.boundAnimate);
    this.controls.update();
    const distance = this.camera.position.distanceTo(this.controls.target);
    if (distance < 25) {
      this.hasZoomedIn = true;
      this.hasEmittedZoomOut = false;
    } else if (distance >= 40 && this.hasZoomedIn && !this.hasEmittedZoomOut) {
      this.hasEmittedZoomOut = true;
      this.ngZone.run(() => {
        this.zoomOutToMap.emit();
      });
    }
    this.renderer.render(this.scene, this.camera);
  }

  private onWindowResize(): void {
    const container = this.canvasContainer.nativeElement;
    const width = container.clientWidth || window.innerWidth;
    const height = container.clientHeight || window.innerHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
    this.requestRender();
  }

  private cleanup(): void {
    this.needsRender = false;
    clearTimeout(this.dampingTimeout);
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    window.removeEventListener('resize', this.boundOnWindowResize);
    if (this.renderer) {
      this.renderer.dispose();
    }
    if (this.controls) {
      this.controls.dispose();
    }
  }

  onBackToMap(): void {
    this.zoomOutToMap.emit();
  }

  onCreateFloorplan(): void {
    this.createFloorplan.emit();
  }

  onUploadFloorplan(): void {
    this.uploadFloorplan.emit();
  }

  async viewInAR(): Promise<void> {
    const url = this.usdzUrl || (this.isDemo ? 'assets/sample-floorplan.usdz' : null);
    if (!url) {
      return;
    }

    // Check if this is a web asset (relative path starting with assets/ or no scheme)
    const isWebAsset = url.startsWith('assets/') ||
                       (!url.startsWith('http://') &&
                        !url.startsWith('https://') &&
                        !url.startsWith('/') &&
                        !url.startsWith('file://') &&
                        !url.startsWith('capacitor://'));

    try {
      if (this.isIOS && Capacitor.isNativePlatform() && !isWebAsset) {
        // Use RoomScanService for native iOS (only for local files or remote URLs)
        await this.roomScanService.openARQuickLook(url);
      } else if (this.isIOS) {
        // Web iOS or web assets - use AR anchor tag
        const anchor = document.createElement('a');
        anchor.setAttribute('rel', 'ar');
        anchor.setAttribute('href', url);
        const img = document.createElement('img');
        anchor.appendChild(img);
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      }
    } catch (error) {
      console.error('Error opening AR Quick Look:', error);
      // Fallback to web AR if native fails
      if (this.isIOS) {
        const anchor = document.createElement('a');
        anchor.setAttribute('rel', 'ar');
        anchor.setAttribute('href', url);
        const img = document.createElement('img');
        anchor.appendChild(img);
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      }
    }
  }

  get canViewInAR(): boolean {
    if (!this.isIOS || this.isLoading) {
      return false;
    }
    const url = this.usdzUrl || (this.isDemo ? 'assets/sample-floorplan.usdz' : null);
    if (!url) {
      return false;
    }
    return Capacitor.getPlatform() === 'ios';
  }
}
